/* KraftCover Bundle & Save - product page selector
   Integrates with custom tb-drawer (window.kcCartDrawer) for cart refresh + open.
   - One global capture-phase submit interceptor (regardless of how many selector instances render)
   - Visual selection highlighting + optional gallery image swap
   - Bundle selected -> POST all bundle items as a batch via /cart/add.js (line item properties)
   - Default option -> let tb-drawer's hijack handle the normal single-item add */

(function () {
  if (customElements.get('kc-bundle-selector')) return;

  const log = (...args) => {
    if (window.location.search.includes('kc_debug=1')) console.log('[kc-bundle]', ...args);
  };

  // Body-level CAPTURE submit handler — fires BEFORE tb-drawer's bubble listener on document.
  function bindGlobalSubmitOnce() {
    if (window.__kcBundleSubmitBound) return;
    window.__kcBundleSubmitBound = true;
    document.body.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.action || !form.action.includes('/cart/add')) return;

      let selector = document.querySelector(`kc-bundle-selector[data-product-form="${form.id}"]`);
      if (!selector) {
        const productForm = form.closest('.product-form') || form.parentElement?.closest('.product-form');
        if (productForm) selector = productForm.querySelector('kc-bundle-selector');
      }
      if (!selector) return;

      const checked = selector.querySelector('.kc-bundle__input:checked');
      if (!checked || !checked.value) return;

      // BYOB guard: requires exactly 2 picks before submit
      const opt = checked.closest('.kc-bundle__option');
      if (opt && opt.dataset.byob && (!selector._byobPicks || selector._byobPicks.length !== 2)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        if (typeof selector._showByobToast === 'function') selector._showByobToast('Önce 2 ürün seç', { assertive: true });
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      log('intercepted bundle submit', form.id, checked.value);
      await selector._doBundleAdd(form);
    }, true);
  }

  // ----- Custom element -----
  class KcBundleSelector extends HTMLElement {
    constructor() {
      super();
      this._galleryOverlay = null;
      this._submitting = false;
      this._byobPicks = []; // [{vid, color, img, name, group}]
      this._byobHydrated = false;
      this._byobHydratePromise = null;
    }

    connectedCallback() {
      bindGlobalSubmitOnce();

      this.addEventListener('change', (e) => {
        if (e.target.classList.contains('kc-bundle__input')) this._onSelect(e.target);
      });
      this.addEventListener('click', (e) => {
        // Swatch click inside BYOB picker
        const swatch = e.target.closest('.kc-byob__swatch');
        if (swatch) {
          e.preventDefault();
          e.stopPropagation();
          this._onSwatchClick(swatch);
          return;
        }
        // Card body click (image/name area) → pick the card's default variant
        const card = e.target.closest('.kc-byob__card');
        if (card) {
          e.preventDefault();
          e.stopPropagation();
          this._onCardClick(card);
          return;
        }
        const label = e.target.closest('.kc-bundle__option');
        if (!label) return;
        const input = label.querySelector('.kc-bundle__input');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      // Keyboard activation for BYOB cards (role=button + tabindex=0 set in
      // _populateCard after hydration). Enter / Space toggle the same default
      // variant pick that mouse click does.
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const card = e.target.closest && e.target.closest('.kc-byob__card');
        if (!card) return;
        // If the focused element is a swatch button, let the browser's native
        // button keydown→click handle it; only intercept when the card itself
        // (not a child button) has focus.
        if (e.target !== card) return;
        e.preventDefault();
        this._onCardClick(card);
      });

      // Pre-warm BYOB pool during browser idle so picker opens hot.
      const prewarm = () => { this._hydrateByobCards().catch(() => {}); };
      if ('requestIdleCallback' in window) {
        requestIdleCallback(prewarm, { timeout: 4000 });
      } else {
        setTimeout(prewarm, 1500);
      }
    }

    _onSelect(input) {
      this.querySelectorAll('.kc-bundle__option').forEach((el) => el.classList.remove('is-checked'));
      const opt = input.closest('.kc-bundle__option');
      if (opt) opt.classList.add('is-checked');

      const imageUrl = opt && opt.dataset.image;
      const bundleHandle = opt && opt.dataset.bundleHandle;
      if (imageUrl) this._showGalleryOverlay(imageUrl, bundleHandle);
      else this._hideGalleryOverlay();

      // BYOB picker show/hide + reset picks if leaving BYOB
      const isByob = !!(opt && opt.dataset.byob);
      const picker = this.querySelector('.kc-byob');
      if (picker) {
        if (isByob) {
          this._hydrateByobCards().catch(() => {});
          requestAnimationFrame(() => picker.classList.add('is-open'));
        } else {
          picker.classList.remove('is-open');
          this._byobPicks = [];
          this._refreshByobUI();
        }
      }

      // Multi-pack customize UI: hide all, show only matching
      this.querySelectorAll('.kc-bundle__customize').forEach((el) => { el.hidden = true; });
      if (opt) {
        const next = opt.nextElementSibling;
        if (next && next.classList && next.classList.contains('kc-bundle__customize')
            && next.dataset.bundleHandle === bundleHandle) {
          next.hidden = false;
          this._initCustomizeUI(next, opt);
        }
      }
    }

    // ───────── Multi-pack customize UI (renkleri özelleştir) — per-slot product aware ─────────
    _initCustomizeUI(container, opt) {
      if (container.dataset.kcInited === '1') return;
      container.dataset.kcInited = '1';

      let productsMap = {};
      try { productsMap = JSON.parse(container.dataset.productsMap || '{}'); } catch (_) {}
      const slotCount = parseInt(container.dataset.slotCount || '0', 10);
      let children = [];
      try { children = JSON.parse(opt.dataset.children || '[]'); } catch (_) {}

      const panel = container.querySelector('.kc-bundle__customize-panel');
      const toggle = container.querySelector('.kc-bundle__customize-toggle');
      if (!panel || !toggle) return;

      toggle.addEventListener('click', () => {
        const open = panel.hidden === false;
        panel.hidden = open;
        toggle.setAttribute('aria-expanded', String(!open));
        toggle.classList.toggle('is-open', !open);
      });

      // Family-swap mode: bundle has multiple products each with 1 variant (Rope Keyring family).
      // Swatches show all family products (cross-product swap), not just the slot's variants.
      const familyMembers = Object.entries(productsMap).filter(([, p]) => p.variants && p.variants.length === 1);
      const isFamilySwap = familyMembers.length >= 2 && familyMembers.length === Object.keys(productsMap).length;

      const slots = children.slice(0, slotCount);
      const slotsHTML = slots.map((child, idx) => {
        const slotPid = String(child.slotProductId || '');
        const slotProduct = productsMap[slotPid];
        if (!slotProduct || !Array.isArray(slotProduct.variants)) {
          return '<div class="kc-bundle__slot" data-slot-idx="' + idx + '">'
            + '<span class="kc-bundle__slot-label">' + (idx + 2) + '.</span>'
            + '<span class="kc-bundle__slot-locked">' + this._escHTML(child.title || '') + '</span></div>';
        }
        let swatchesHTML;
        let productLabel;
        if (isFamilySwap) {
          // Render each family product as a swatch — picking swaps slot to that product
          swatchesHTML = familyMembers.map(([pid, prod]) => {
            const v = prod.variants[0];
            const isActive = v.id === child.id;
            const oos = v.available === false;
            const shortColor = this._extractColorLabel(prod.title);
            return '<button type="button" class="kc-bundle__slot-swatch'
              + (isActive ? ' is-active' : '')
              + (oos ? ' is-oos' : '')
              + '" data-vid="' + v.id
              + '" data-pid="' + pid
              + '" data-vtitle="' + this._escAttr(prod.title)
              + '" data-vprice="' + v.price + '"'
              + (oos ? ' disabled' : '')
              + ' title="' + this._escAttr(prod.title) + '">'
              + this._escHTML(shortColor)
              + '</button>';
          }).join('');
          productLabel = '';
        } else {
          // Standard intra-product variant swap
          swatchesHTML = slotProduct.variants.map((v) => {
            const isActive = v.id === child.id;
            const oos = v.available === false;
            return '<button type="button" class="kc-bundle__slot-swatch'
              + (isActive ? ' is-active' : '')
              + (oos ? ' is-oos' : '')
              + '" data-vid="' + v.id
              + '" data-vtitle="' + this._escAttr((slotProduct.title || '') + ' — ' + v.title)
              + '" data-vprice="' + v.price + '"'
              + (oos ? ' disabled' : '')
              + ' title="' + this._escAttr(v.title) + '">'
              + this._escHTML(this._compactVariantLabel(v))
              + '</button>';
          }).join('');
          productLabel = slotProduct.title ? '<span class="kc-bundle__slot-product">' + this._escHTML(this._shortTitle(slotProduct.title)) + '</span>' : '';
        }
        return '<div class="kc-bundle__slot" data-slot-idx="' + idx + '">'
          + '<div class="kc-bundle__slot-header">'
          + '<span class="kc-bundle__slot-label">' + (idx + 2) + '.</span>'
          + productLabel
          + '</div>'
          + '<div class="kc-bundle__slot-swatches">' + swatchesHTML + '</div>'
          + '</div>';
      }).join('');
      panel.innerHTML = slotsHTML;

      panel.addEventListener('click', (e) => {
        const sw = e.target.closest('.kc-bundle__slot-swatch');
        if (!sw || sw.disabled) return;
        const slot = sw.closest('.kc-bundle__slot');
        if (!slot) return;
        const slotIdx = parseInt(slot.dataset.slotIdx, 10);
        const vid = parseInt(sw.dataset.vid, 10);
        const vtitle = sw.dataset.vtitle || '';
        const swappedPid = sw.dataset.pid ? parseInt(sw.dataset.pid, 10) : null;
        slot.querySelectorAll('.kc-bundle__slot-swatch').forEach((s) => s.classList.remove('is-active'));
        sw.classList.add('is-active');
        let arr = [];
        try { arr = JSON.parse(opt.dataset.children || '[]'); } catch (_) {}
        if (arr[slotIdx]) {
          arr[slotIdx].id = vid;
          arr[slotIdx].title = vtitle;
          if (swappedPid) arr[slotIdx].slotProductId = swappedPid;
        }
        opt.dataset.children = JSON.stringify(arr);
        this._recalcBundleTotals(opt, container);
      });
    }

    _compactVariantLabel(v) {
      // Prefer "Option1 · Option2" compact form when both options exist
      if (v.option1 && v.option2 && v.option2 !== 'Default Title') {
        const o1 = String(v.option1).replace(/\s*\(.*\)\s*$/, '');
        return o1 + ' · ' + v.option2;
      }
      return v.title;
    }

    _shortTitle(title) {
      return String(title).split(' (')[0].split(' / ')[0].trim();
    }

    _extractColorLabel(productTitle) {
      // For family products like "Everyday Rope Keyring (Koyu Mavi)" → "Koyu Mavi"
      const t = String(productTitle);
      const paren = t.match(/\(([^)]+)\)\s*$/);
      if (paren) return paren[1].trim();
      const slash = t.split(' / ');
      if (slash.length > 1) return slash[slash.length - 1].trim();
      return this._shortTitle(t);
    }

    _recalcBundleTotals(opt, container) {
      let productsMap = {};
      try { productsMap = JSON.parse(container.dataset.productsMap || '{}'); } catch (_) {}
      const parentDefaultVid = parseInt(container.dataset.parentDefaultVid || '0', 10);
      let children = [];
      try { children = JSON.parse(opt.dataset.children || '[]'); } catch (_) {}

      const variantPrice = (vid, slotPid) => {
        const productKey = slotPid ? String(slotPid) : null;
        // Try slot's product first
        if (productKey && productsMap[productKey]) {
          const v = productsMap[productKey].variants.find((x) => x.id === vid);
          if (v) return v.price;
        }
        // Fallback: search all products
        for (const k in productsMap) {
          const v = productsMap[k].variants.find((x) => x.id === vid);
          if (v) return v.price;
        }
        return 0;
      };

      const parentPrice = variantPrice(parentDefaultVid);
      const childPrices = children.reduce((s, c) => s + variantPrice(c.id, c.slotProductId), 0);
      const total = parentPrice + childPrices;
      const discount = parseFloat(opt.dataset.discount || '0');
      const saving = Math.round(total * discount / 100);
      const discounted = total - saving;

      opt.dataset.bundleOriginal = String(total);
      opt.dataset.bundleSaving = String(saving);
      opt.dataset.bundleDiscounted = String(discounted);
    }

    _escAttr(s) { return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;'); }
    _escHTML(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

    // ----- Lazy product hydration -----
    // Liquid's `all_products[]` is capped at 20 unique products per template.
    // The BYOB pool is now 25+ handles, so we hydrate cards client-side via
    // /products/HANDLE.js (no cache limit, parallel fetch, session-cached).
    async _hydrateByobCards() {
      if (this._byobHydrated) return;
      if (this._byobHydratePromise) return this._byobHydratePromise;

      const cards = Array.from(this.querySelectorAll('.kc-byob__card--lazy'));
      if (!cards.length) { this._byobHydrated = true; return; }

      const allHandles = new Set();
      cards.forEach((c) => {
        (c.dataset.handles || '').split(',').filter(Boolean).forEach((h) => allHandles.add(h));
      });

      const cache = (window.__kcProductCache = window.__kcProductCache || {});
      const fetchOne = async (h) => {
        if (Object.prototype.hasOwnProperty.call(cache, h)) return [h, cache[h]];
        try {
          const r = await fetch(`/products/${encodeURIComponent(h)}.js`, { credentials: 'same-origin' });
          if (!r.ok) { cache[h] = null; return [h, null]; }
          const p = await r.json();
          cache[h] = p;
          return [h, p];
        } catch (_) { cache[h] = null; return [h, null]; }
      };

      this._byobHydratePromise = (async () => {
        // Concurrency cap: 25+ parallel /products/X.js fetches saturate slow
        // mobile connections. Pool of 4 keeps things fast without burst.
        const handles = Array.from(allHandles);
        const POOL = 4;
        const byHandle = {};
        let idx = 0;
        const workers = Array.from({ length: Math.min(POOL, handles.length) }, async () => {
          while (idx < handles.length) {
            const h = handles[idx++];
            const [hh, p] = await fetchOne(h);
            byHandle[hh] = p;
          }
        });
        await Promise.all(workers);
        cards.forEach((card) => this._populateCard(card, byHandle));
        // Only mark hydrated if at least one card got real data — otherwise
        // a transient network blip would permanently dead-state the picker.
        const anyHydrated = Object.values(byHandle).some((p) => p);
        if (anyHydrated) this._byobHydrated = true;
      })();

      try { await this._byobHydratePromise; }
      finally { this._byobHydratePromise = null; }
    }

    _populateCard(card, byHandle) {
      const handles = (card.dataset.handles || '').split(',').filter(Boolean);
      const groupName = card.dataset.name || '';
      const firstP = byHandle[handles[0]];

      if (!firstP) {
        card.style.display = 'none';
        card.removeAttribute('aria-busy');
        return;
      }

      // Hide card entirely when every variant / every product in the group is OOS.
      const allOos = handles.length > 1
        ? handles.every((h) => { const p = byHandle[h]; return !p || !p.available; })
        : (firstP.variants || []).every((v) => !v.available);
      if (allOos) {
        card.style.display = 'none';
        card.removeAttribute('aria-busy');
        return;
      }

      // Card thumbnail
      const imgWrap = card.querySelector('.kc-byob__card-img');
      if (imgWrap) {
        imgWrap.innerHTML = '';
        const img = document.createElement('img');
        img.src = this._sizedImg(firstP.featured_image, 200);
        img.alt = groupName;
        img.loading = 'lazy';
        imgWrap.appendChild(img);
      }

      // Default click data — first AVAILABLE variant so card-body click never sends OOS to cart.
      const firstAvailP = handles.length > 1
        ? (handles.map((h) => byHandle[h]).find((p) => p && p.available) || firstP)
        : firstP;
      const firstAvailV = (firstAvailP.variants || []).find((v) => v.available)
        || (firstAvailP.variants && firstAvailP.variants[0]) || {};
      card.dataset.defaultVid = String(firstAvailV.id || '');
      card.dataset.defaultColor = handles.length > 1 ? (firstAvailP.title || '') : (firstAvailV.title || '');
      card.dataset.defaultImg = this._sizedImg(firstAvailP.featured_image, 80);
      card.dataset.defaultName = groupName;

      // Swatches — show up to MAX_VISIBLE, overflow as "+N" pill.
      // Keeps card height consistent across products with different variant counts.
      const MAX_VISIBLE = 4;
      const swEl = card.querySelector('.kc-byob__swatches');
      if (swEl) {
        const frag = document.createDocumentFragment();
        let total = 0;
        let emitted = 0;
        const pushSwatch = (spec) => {
          if (emitted < MAX_VISIBLE) {
            frag.appendChild(this._buildSwatch(spec));
            emitted++;
          }
          total++;
        };
        if (handles.length > 1) {
          // Multi-product group: each product = one color
          handles.forEach((h) => {
            const p = byHandle[h];
            if (!p) return;
            const v = (p.variants && p.variants[0]) || {};
            if (!v.id) return;
            const colorLbl = this._extractColor(p.title || '', groupName);
            const swImg = this._sizedImg(p.featured_image, 80);
            pushSwatch({
              vid: v.id, color: colorLbl, img: swImg, name: groupName,
              hasUniqueImg: true, tint: '', available: !!p.available
            });
          });
        } else {
          // Single-product group: iterate variants
          const fallbackImg = this._sizedImg(firstP.featured_image, 80);
          (firstP.variants || []).forEach((v) => {
            const vSrc = v.featured_image && v.featured_image.src;
            const hasUniqueImg = !!vSrc;
            const swImg = hasUniqueImg ? this._sizedImg(vSrc, 80) : fallbackImg;
            const tint = hasUniqueImg ? '' : this._colorTint(v.title || '');
            pushSwatch({
              vid: v.id, color: v.title || '', img: swImg, name: groupName,
              hasUniqueImg, tint, available: !!v.available
            });
          });
        }
        if (total > MAX_VISIBLE) {
          const more = document.createElement('span');
          more.className = 'kc-byob__swatch-more';
          more.textContent = `+${total - MAX_VISIBLE}`;
          more.setAttribute('aria-label', `${total - MAX_VISIBLE} daha fazla renk`);
          frag.appendChild(more);
        }
        swEl.innerHTML = '';
        swEl.appendChild(frag);
      }

      card.classList.remove('kc-byob__card--lazy');
      card.removeAttribute('aria-busy');
      // Keyboard accessibility: only activate after hydration so Tab doesn't
      // land on an empty skeleton card.
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');
      const lblName = card.dataset.name || '';
      if (lblName) card.setAttribute('aria-label', `${lblName} paketine ekle`);
    }

    _buildSwatch({ vid, color, img, name, hasUniqueImg, tint, available = true }) {
      const btn = document.createElement('button');
      btn.type = 'button';
      let cls = 'kc-byob__swatch' + (hasUniqueImg ? '' : ' kc-byob__swatch--solid');
      if (!available) cls += ' kc-byob__swatch--oos';
      btn.className = cls;
      btn.dataset.vid = String(vid);
      btn.dataset.color = color;
      btn.dataset.img = img;
      btn.dataset.name = name;
      btn.title = available ? color : `${color} — Tükendi`;
      btn.setAttribute('aria-label', available ? `${name}: ${color}` : `${name}: ${color} — Tükendi`);
      if (!available) btn.disabled = true;
      if (!hasUniqueImg && tint) {
        btn.style.background = tint;
        btn.style.borderColor = tint;
      }
      if (hasUniqueImg && img) {
        const i = document.createElement('img');
        i.src = img;
        i.alt = color;
        i.loading = 'lazy';
        btn.appendChild(i);
      }
      return btn;
    }

    _sizedImg(url, w) {
      if (!url) return '';
      const u = String(url);
      // Strip any existing width param then re-append
      const cleaned = u.replace(/([?&])width=\d+&?/g, '$1').replace(/[?&]$/, '');
      return cleaned + (cleaned.includes('?') ? '&' : '?') + `width=${w}`;
    }

    _extractColor(title, groupName) {
      const c = title.replace(groupName, '').replace(/[()\\\/]/g, '').replace(/Çapraz Çanta/i, '').trim();
      return c || title;
    }

    _colorTint(title) {
      const t = (title || '').toLowerCase();
      if (/siyah|noir|blackline/.test(t)) return '#1a1a1a';
      if (/antrasit|titan/.test(t)) return '#3a3a3a';
      if (/gri|ash/.test(t)) return '#9a9a9a';
      if (/yeşil|forest|grove/.test(t)) return '#2d6a3f';
      if (/kahverengi|amber|dune|bej/.test(t)) return '#a07a52';
      if (/lacivert|mavi/.test(t)) return '#1f3a5f';
      if (/turuncu/.test(t)) return '#e07b3a';
      if (/pembe/.test(t)) return '#d96a8e';
      return '';
    }

    _onCardClick(card) {
      const group = card.dataset.group;
      // Toggle: if this card already picked → remove (deselect)
      const existingIdx = this._byobPicks.findIndex((p) => p.group === group);
      if (existingIdx >= 0) {
        this._byobPicks.splice(existingIdx, 1);
        // Restore card thumbnail to default
        const cardImg = card.querySelector('.kc-byob__card-img img');
        if (cardImg && card.dataset.cardOriginalSrc) cardImg.src = card.dataset.cardOriginalSrc;
        this._refreshByobUI();
        return;
      }
      const vid = parseInt(card.dataset.defaultVid, 10);
      if (!vid) return;
      const fauxSwatch = {
        dataset: {
          vid: String(vid),
          color: card.dataset.defaultColor || '',
          img: card.dataset.defaultImg || '',
          name: card.dataset.defaultName || '',
        },
        closest: () => card,
      };
      this._onSwatchClick(fauxSwatch);
    }

    _onSwatchClick(swatch) {
      const vid = parseInt(swatch.dataset.vid, 10);
      if (!vid) return;
      const card = swatch.closest('.kc-byob__card');
      const group = card ? card.dataset.group : '';
      const color = swatch.dataset.color || '';
      const name = swatch.dataset.name || '';
      const img = swatch.dataset.img || '';

      // Toggle: if same vid already picked → remove
      const existingIdx = this._byobPicks.findIndex((p) => p.vid === vid);
      if (existingIdx >= 0) {
        this._byobPicks.splice(existingIdx, 1);
        this._refreshByobUI();
        return;
      }

      // Same group already picked (different color) → REPLACE that pick
      const sameGroupIdx = this._byobPicks.findIndex((p) => p.group === group);
      if (sameGroupIdx >= 0) {
        this._byobPicks[sameGroupIdx] = { vid, color, img, name, group };
        this._refreshByobUI();
        return;
      }

      // New pick: only allow if under limit (2)
      if (this._byobPicks.length >= 2) {
        this._showByobToast('En fazla 2 ürün seçebilirsin');
        return;
      }

      this._byobPicks.push({ vid, color, img, name, group });
      this._refreshByobUI();
    }

    _refreshByobUI() {
      const picker = this.querySelector('.kc-byob');
      if (!picker) return;
      const countEl = picker.querySelector('.kc-byob__count');
      if (countEl) countEl.textContent = String(this._byobPicks.length);
      picker.classList.toggle('is-complete', this._byobPicks.length === 2);

      // Live total: when both picks are in, surface the savings amount inline
      // in the counter row. Premium pattern — locks the buy decision before
      // the user has to ATC and discover the math in the drawer.
      const totalEl = picker.querySelector('.kc-byob__total');
      if (totalEl) {
        if (this._byobPicks.length === 2) {
          const original = this._byobPicks.reduce((s, p) => s + this._lookupVariantPrice(p.vid), 0);
          const byobOpt = this.querySelector('.kc-bundle__option[data-byob]');
          const byobPct = parseFloat(byobOpt && byobOpt.dataset.discount) || 0;
          if (original > 0 && byobPct > 0) {
            const saving = Math.round(original * byobPct / 100);
            totalEl.innerHTML = `· tasarruf <strong>${this._fmtMoney(saving)}</strong>`;
            totalEl.hidden = false;
          } else {
            totalEl.hidden = true;
          }
        } else {
          totalEl.hidden = true;
          totalEl.textContent = '';
        }
      }

      const pickedVids = new Set(this._byobPicks.map((p) => p.vid));
      const pickedByGroup = new Map(this._byobPicks.map((p) => [p.group, p]));

      picker.querySelectorAll('.kc-byob__card').forEach((card) => {
        const group = card.dataset.group;
        const pick = pickedByGroup.get(group);
        const img = card.querySelector('.kc-byob__card-img img');
        if (img) {
          if (!card.dataset.cardOriginalSrc) card.dataset.cardOriginalSrc = img.src;
          if (pick && pick.img) {
            // Use a larger version of the swatch image for the card thumbnail
            const big = pick.img.replace(/(\?|&)width=\d+/, '$1width=200');
            if (img.src !== big) img.src = big;
          } else if (card.dataset.cardOriginalSrc) {
            img.src = card.dataset.cardOriginalSrc;
          }
        }
        card.classList.toggle('is-picked', !!pick);
        // Mirror the visual selection state for screen readers.
        if (card.hasAttribute('aria-pressed')) {
          card.setAttribute('aria-pressed', pick ? 'true' : 'false');
        }
      });
      picker.querySelectorAll('.kc-byob__swatch').forEach((sw) => {
        sw.classList.toggle('is-picked', pickedVids.has(parseInt(sw.dataset.vid, 10)));
      });
    }

    // Find a variant's price (cents) by scanning the lazy-load product cache.
    // Pool is small (~15 products × ~5 variants), so a linear scan is fine.
    _lookupVariantPrice(vid) {
      const cache = window.__kcProductCache || {};
      for (const handle in cache) {
        const p = cache[handle];
        if (!p || !p.variants) continue;
        const v = p.variants.find((x) => x.id === vid);
        if (v) return parseInt(v.price, 10) || 0;
      }
      return 0;
    }

    // TR currency formatter, cached. Mirrors cart-drawer.js so amounts read
    // identically across PDP picker and cart drawer.
    _fmtMoney(cents) {
      if (typeof cents !== 'number') cents = parseInt(cents) || 0;
      if (!this._moneyFmt) {
        try {
          this._moneyFmt = new Intl.NumberFormat('tr-TR', {
            style: 'currency', currency: 'TRY',
            minimumFractionDigits: 2, maximumFractionDigits: 2,
          });
        } catch (_) { this._moneyFmt = null; }
      }
      if (this._moneyFmt) return this._moneyFmt.format(cents / 100);
      return (cents / 100).toFixed(2).replace('.', ',') + ' ₺';
    }

    _showByobToast(msg, opts) {
      const assertive = !!(opts && opts.assertive);
      let t = this.querySelector('.kc-byob__toast');
      if (!t) {
        t = document.createElement('div');
        t.className = 'kc-byob__toast';
        const picker = this.querySelector('.kc-byob');
        (picker || this).appendChild(t);
      }
      // ARIA: blocking errors (couldn't add to cart, must pick 2 first) are
      // announced as alerts; informational ("max 2 reached") stays polite so
      // it doesn't interrupt the user mid-action.
      t.setAttribute('role', assertive ? 'alert' : 'status');
      t.setAttribute('aria-live', assertive ? 'assertive' : 'polite');
      t.textContent = msg;
      t.classList.add('is-visible');
      clearTimeout(this._byobToastTimer);
      this._byobToastTimer = setTimeout(() => t.classList.remove('is-visible'), 2000);
    }

    // Gallery image swap — disabled for v1 (Flickity carousel conflicts).
    // To revisit: implement via Flickity's prepend()+select() API after main launch.
    _showGalleryOverlay() {}
    _hideGalleryOverlay() {}

    async _doBundleAdd(form) {
      if (this._submitting) { log('already submitting, ignoring'); return; }

      const checked = this.querySelector('.kc-bundle__input:checked');
      if (!checked || !checked.value) return;

      const opt = checked.closest('.kc-bundle__option');
      const bundleHandle = opt.dataset.bundleHandle || '';
      const bundleTitle = opt.querySelector('.kc-bundle__title')?.textContent?.trim() || 'Bundle';
      // 5-char base36 random was 60M space — under heavy click-spam two
      // adds in the same ms could collide and silently merge across bundles
      // in Cart Transform. crypto.randomUUID is universally available on
      // modern browsers; fall back to the legacy pattern if not.
      const uidRand = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const bundleUid = `${bundleHandle || 'bundle'}-${uidRand}`;

      // BYOB: build children from user picks instead of static dataset attribute
      let children = [];
      const isByob = !!opt.dataset.byob;
      if (isByob) {
        if (!this._byobPicks || this._byobPicks.length !== 2) {
          this._showByobToast('Önce 2 ürün seç', { assertive: true });
          return;
        }
        children = this._byobPicks.map((p) => ({ id: p.vid, title: `${p.name} (${p.color})`, image: p.img }));
      } else {
        try { children = JSON.parse(opt.dataset.children || '[]'); } catch (_) {}
      }

      const variantId = parseInt(form.querySelector('[name="id"]')?.value || this.dataset.defaultVariant, 10);
      const qty = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10) || 1;

      const items = [
        {
          id: variantId,
          quantity: qty,
          properties: {
            _bundle_handle: bundleHandle,
            _bundle_label: bundleTitle,
            _bundle_role: 'parent',
            _bundle_uid: bundleUid,
          },
        },
        ...children
          .filter((c) => c && c.id)
          .map((c, idx) => ({
            id: parseInt(c.id, 10),
            quantity: qty,
            properties: {
              _bundle_handle: bundleHandle,
              _bundle_label: bundleTitle,
              _bundle_role: 'child',
              _bundle_uid: bundleUid,
              _bundle_slot: String(idx),
            },
          })),
      ];

      log('POST /cart/add.js', items.length, 'items');

      this._submitting = true;
      this._setLoading(true, form);
      // Open the drawer + start the progress bar IMMEDIATELY so the
      // user sees feedback while bundle add is in flight (multi-line POST
      // + source-key persist round-trip can take 1–2s on slow networks).
      if (window.kcCartDrawer) {
        if (typeof window.kcCartDrawer.open === 'function') window.kcCartDrawer.open();
        if (typeof window.kcCartDrawer.setAdding === 'function') window.kcCartDrawer.setAdding(true);
        // Inject a "preparing" skeleton with bundle title + image so the
        // drawer reads as "your selection is being added" instead of just
        // showing the old cart contents during the multi-line round-trip.
        if (typeof window.kcCartDrawer.injectPending === 'function') {
          const bundleImg = (opt.dataset.image || '').trim();
          window.kcCartDrawer.injectPending({
            title: bundleTitle,
            image: bundleImg,
            isBundle: true,
          });
        }
      }
      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) {
          // Shopify's /cart/add.js with items[] is atomic — if any item fails
          // (out of stock, max-quantity hit, etc.) NONE are added. Surface a
          // confident Turkish message identifying the problem item if we can
          // parse `errors` map (Shopify returns `{ status, message,
          // description, errors: { "items": ["Tech Kit Noir is sold out"] } }`).
          const err = await r.json().catch(() => ({}));
          let msg = err.description || err.message || 'Paket eklenemedi. Tekrar dene.';
          // Pull a more specific reason from errors map when available.
          if (err.errors && typeof err.errors === 'object') {
            const errVals = Object.values(err.errors).flat().filter(Boolean);
            if (errVals.length) msg = errVals.join(' · ');
          }
          // Common 422 patterns → confident, action-first copy.
          if (/sold out|out of stock|stok/i.test(msg)) {
            msg = 'Paketteki bir ürün tükendi. Farklı bir paket dene.';
          } else if (/maximum|max|limit/i.test(msg)) {
            msg = 'Sepetinde bu üründen maksimum miktar var.';
          }
          throw new Error(msg);
        }
        // Capture source line keys from response, persist as cart attribute so
        // tb-drawer can do bundle-aware multi-update on qty change / delete.
        // Retry on flaky-network failures: if this write fails the bundle is
        // still in cart but qty/remove falls back to legacy single-line path.
        try {
          const body = await r.json();
          const addedItems = Array.isArray(body) ? body : (body.items || []);
          const keys = addedItems.map((i) => i && i.key).filter(Boolean);
          if (keys.length) {
            // Build variants summary ONLY for multi-pack bundles (customize container exists).
            // Non-multi-pack bundles (tech-bundle, daily-carry-set, etc.) keep their existing
            // Function-set _bundle_subtitle which includes parent product title.
            let variantsSummary = '';
            try {
              const opt = checked.closest('.kc-bundle__option');
              const customize = opt && opt.nextElementSibling;
              const isMultiPack = customize && customize.classList && customize.classList.contains('kc-bundle__customize');
              if (isMultiPack) {
                let pmap = {};
                try { pmap = JSON.parse(customize.dataset.productsMap || '{}'); } catch (_) {}
                // Family-swap detection: every product in pmap has only 1 variant → label by product (color), not variant.
                const allProds = Object.values(pmap);
                const isFamilySwap = allProds.length >= 2 && allProds.every((p) => p.variants && p.variants.length === 1);
                const self = this;
                const labelFor = (vid, slotPid) => {
                  // Try slotPid product first
                  const keyTry = slotPid ? String(slotPid) : null;
                  const prod = (keyTry && pmap[keyTry]) || null;
                  if (prod) {
                    if (isFamilySwap || prod.variants.length === 1) return self._extractColorLabel(prod.title);
                    const v = prod.variants.find((x) => x.id === vid);
                    if (v) return v.title;
                  }
                  // Fallback: search all
                  for (const k in pmap) {
                    const v = (pmap[k].variants || []).find((x) => x.id === vid);
                    if (v) {
                      if (isFamilySwap || pmap[k].variants.length === 1) return self._extractColorLabel(pmap[k].title);
                      return v.title;
                    }
                  }
                  return '';
                };
                const parentForm = form.querySelector('[name="id"]');
                const parentVid = parentForm ? parseInt(parentForm.value, 10) : 0;
                const parentLabel = labelFor(parentVid, null);
                const allItems = [];
                if (parentVid && parentLabel) allItems.push(parentLabel);
                children.filter((c) => c && c.id).forEach((c) => {
                  const t = labelFor(parseInt(c.id, 10), c.slotProductId) || (c.title || '').split(' — ').pop();
                  if (t) allItems.push(t);
                });
                const grouped = {};
                allItems.forEach((t) => { grouped[t] = (grouped[t] || 0) + 1; });
                variantsSummary = Object.entries(grouped)
                  .map(([t, n]) => (n > 1 ? n + '× ' + t : t))
                  .join(' · ');
              }
            } catch (e) { log('variants summary build failed', e); }

            const attrs = { [`_kc_bundle_${bundleUid}`]: keys.join(',') };
            if (variantsSummary) attrs[`_kc_bundle_${bundleUid}_variants`] = variantsSummary;
            const attrPayload = { attributes: attrs };
            let attrOk = false;
            for (let attempt = 0; attempt < 3 && !attrOk; attempt++) {
              if (attempt > 0) await new Promise((r) => setTimeout(r, 400 * attempt));
              try {
                const ar = await fetch('/cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify(attrPayload),
                });
                if (ar.ok) attrOk = true;
              } catch (_) { /* retry */ }
            }
            if (!attrOk) {
              log('source-key store failed after 3 attempts');
              // Non-fatal — bundle is in cart, just degraded qty/remove UX.
              // Observability: persist a marker in localStorage so this can be
              // surfaced (manually or via analytics) instead of going silent.
              try {
                localStorage.setItem(
                  `kc_srckey_lost_${bundleUid}`,
                  JSON.stringify({ uid: bundleUid, ts: Date.now() })
                );
              } catch (_) { /* private mode / quota — accept loss */ }
              if (window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('kc:srckey-lost', { detail: { uid: bundleUid } }));
              }
            }
          }
        } catch (e) { log('source-key store failed', e); }

        if (window.kcCartDrawer && typeof window.kcCartDrawer.refresh === 'function') {
          await window.kcCartDrawer.refresh();
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { openMiniCart: true },
          }));
        }
      } catch (err) {
        console.error('[kc-bundle]', err);
        // Toast keeps brand voice consistent — native alert() in a premium
        // cart breaks immersion. Assertive priority so SR users get it.
        this._showByobToast(err.message || 'Paket eklenemedi. Tekrar dene.', { assertive: true });
      } finally {
        this._submitting = false;
        this._setLoading(false, form);
        if (window.kcCartDrawer) {
          if (typeof window.kcCartDrawer.setAdding === 'function') window.kcCartDrawer.setAdding(false);
          // On success refresh() already blasted the scroll innerHTML; on
          // error the pending card would otherwise linger forever.
          if (typeof window.kcCartDrawer.clearPending === 'function') window.kcCartDrawer.clearPending();
        }
      }
    }

    _setLoading(state, form) {
      this.classList.toggle('is-loading', state);
      const btn = form && form.querySelector('[data-product-add-to-cart-button]');
      if (btn) {
        btn.disabled = state;
        btn.classList.toggle('is-loading', state);
      }
    }
  }

  customElements.define('kc-bundle-selector', KcBundleSelector);
})();
